import React, { useState } from 'react';
import capitalize from 'lodash/capitalize';
import memoize from 'lodash/memoize';
import { NEW_DATE_TIME_FORMAT } from '@atlassian/cssxp-typescommon';
import { DEFAULT_PAGE, DEFAULT_ROWS_PER_PAGE } from '@atlassian/cssxp-utils-entitlement';
import { DynamicResultTable, FormattedDate } from '@css-xp/base';
import { ChargeQuantity, ChargeQuantityUsersUpdate } from '@css-xp/graphtypes/src/generated-types-new';
import { ListUtil } from '@css-xp/entitlements-v2/entitlement-page/src/tabs/utils/list';
import {
  emptyStateProp,
  errorStateProp,
  NoData,
  TableHeading,
  emptyStatePropForUserHistoryTable,
} from '@css-xp/ppc-common';
import { getChargeElementText, ChargeElementType } from '@css-xp/ppc-utils';
import { useFetchUsageHistoryForEntitlementPPCQuery } from './use-fetch-user-history-for-entitlement-ppc-query';
import './product-usage-table.scss';

interface Props {
  id: string;
  highestUsageParentEntitlementId: string;
  transactionAccountId: string;
  chargeElement: string | undefined | null;
  tableHeading: string;
  isDesignIteration1Enabled?: boolean;
}

export const ProductUsageTable = ({
  id,
  highestUsageParentEntitlementId,
  transactionAccountId,
  chargeElement = ChargeElementType.USER,
  tableHeading,
  isDesignIteration1Enabled,
}: Props) => {
  const [currentPage, setCurrentPage] = useState(DEFAULT_PAGE);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

  const { loading, data, error } = useFetchUsageHistoryForEntitlementPPCQuery({
    entitlementId: id,
    transactionAccountId: transactionAccountId,
    highestUsageParentEntitlementId: highestUsageParentEntitlementId,
  });

  const chargeQuantities = data?.entitlementDetails?.userHistory?.results ?? [];

  const labelNameBasedOnChargeElementType = getChargeElementText(chargeElement);

  const list = new ListUtil(chargeQuantities);
  const defaultList = list.get({ first: rowsPerPage - 1, offset: currentPage - 1 }) as (ChargeQuantity & {
    id: string;
  })[];
  const [resultList, setResultList] = useState(defaultList);

  React.useEffect(() => {
    if (!loading) {
      setResultList(defaultList);
    }
  }, [loading]);

  const refetch = ({ offset, first }: { offset: number; first: number }) => {
    setResultList(list.get({ first, offset }) as (ChargeQuantity & { id: string })[]);
  };

  const onPageChange = (newPage: number) => {
    if (currentPage === newPage) {
      return;
    }
    setCurrentPage(newPage);

    refetch({
      offset: newPage !== 1 ? newPage * rowsPerPage - rowsPerPage : 0,
      first: rowsPerPage - 1,
    });
  };

  const onRowPerPageChange = (newRowPerPage: number) => {
    if (newRowPerPage === rowsPerPage) {
      return;
    }
    setRowsPerPage(newRowPerPage);
    refetch({
      offset: 0,
      first: newRowPerPage - 1,
    });
  };

  const getUserChangeString = (
    usersUpdate: ChargeQuantityUsersUpdate,
    isAddColumn: boolean,
    isRemoveColumn: boolean
  ) => {
    if (usersUpdate.isCreated && isAddColumn) {
      return `Created`;
    }
    if (usersUpdate.isAdded && isAddColumn) {
      return `+ ${usersUpdate.changeBy}`;
    }
    if (usersUpdate.isRemoved && isRemoveColumn) {
      return `- ${usersUpdate.changeBy}`;
    }
    return '';
  };

  const getRows = () => {
    return resultList.map((rowList) => {
      return {
        key: rowList.id,
        cells: [
          {
            content: rowList?.lastUpdatedAt ? (
              <FormattedDate date={rowList.lastUpdatedAt} format={NEW_DATE_TIME_FORMAT} />
            ) : (
              <NoData />
            ),
          },
          {
            content: (
              <span className={isDesignIteration1Enabled ? '' : 'user-change-add'}>
                {rowList?.usersUpdate ? getUserChangeString(rowList?.usersUpdate, true, false) : <NoData />}
              </span>
            ),
          },
          ...(chargeElement !== ChargeElementType.VIRTUAL_AGENT
            ? [
                {
                  content: (
                    <span className="user-change-remove">
                      {rowList?.usersUpdate ? getUserChangeString(rowList?.usersUpdate, false, true) : <NoData />}
                    </span>
                  ),
                },
              ]
            : []),
          {
            content: rowList?.quantity?.toString() ?? <NoData />,
          },
        ],
      };
    });
  };

  const getHead = memoize(() => {
    return {
      cells: [
        {
          content: 'Date',
          width: 25,
        },
        {
          content: `${capitalize(labelNameBasedOnChargeElementType)} ${
            chargeElement === ChargeElementType.VIRTUAL_AGENT ? 'created' : 'added'
          }`,
          width: 25,
        },
        ...(chargeElement !== ChargeElementType.VIRTUAL_AGENT
          ? [
              {
                content: `${capitalize(labelNameBasedOnChargeElementType)} removed`,
                width: 25,
              },
            ]
          : []),
        {
          content: `Total ${labelNameBasedOnChargeElementType}`,
          width: 25,
        },
      ],
    };
  });

  return (
    <DynamicResultTable
      isLoading={loading}
      testId="product-usage-table"
      beforeTable={isDesignIteration1Enabled ? null : <TableHeading>{tableHeading}</TableHeading>}
      head={getHead()}
      rows={getRows()}
      emptyStateProps={isDesignIteration1Enabled ? emptyStatePropForUserHistoryTable : emptyStateProp}
      totalCount={list.totalCount}
      currentPage={currentPage}
      rowsPerPage={rowsPerPage}
      onPageChange={onPageChange}
      isPaginationEnabled={list.totalCount > DEFAULT_ROWS_PER_PAGE}
      onRowChange={onRowPerPageChange}
      errorStateProps={errorStateProp}
      error={error?.message}
      hideHeadOnEmptyState={isDesignIteration1Enabled}
    />
  );
};
